import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../../supabase';
import styles from './MyPage.module.css';
import TabMenu from '../components/TabMenu';
import Button from '../../../../components/common/Button';

function MyPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [seedMoneyHistory, setSeedMoneyHistory] = useState([]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
        fetchUserIdeas(session.user.id);
        fetchSeedMoneyHistory(session.user.id);
      } else {
        navigate('/admin/login');
      }
    });
  }, [navigate]);

  const fetchUserProfile = async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) return;
    setUserProfile(data);
  };

  const fetchUserIdeas = async (userId) => {
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('user_id', userId);

    if (error) return;
    setIdeas(data);
  };

  const fetchSeedMoneyHistory = async (userId) => {
    const { data, error } = await supabase
      .from('seed_money_histories')
      .select(`
        *,
        ideas (
          title,
          status
        ),
        idea_investments (
          value
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) return;
    setSeedMoneyHistory(data);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/project/plabthon');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getHistoryTypeLabel = (type) => {
    switch (type) {
      case 'idea_registration': return {
        label: '프로덕트 등록',
        className: styles.typeRegistration
      };
      case 'investment': return {
        label: '투자',
        className: styles.typeInvestment
      };
      default: return {
        label: '',
        className: ''
      };
    }
  };

  const getStatusBadge = (status) => {
    if (!status || status === 'pending') return null;

    const statusMap = {
      team_building: { label: '멤버 모집중', className: styles.statusTeamBuilding },
      team_confirmed: { label: '멤버 모집 완료', className: styles.statusTeamConfirmed },
      in_progress: { label: '달려!🔥', className: styles.statusInProgress },
      cancel: { label: '취소됨', className: styles.statusCancel }
    };

    const statusInfo = statusMap[status];
    if (!statusInfo) return null;

    return (
      <span className={`${styles.statusBadge} ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <div className={styles.header}>
          <h1>MY</h1>
        </div>
        <div className={styles.section}>
          <div className={styles.myTotalValueContainer}>
            <h2>총 자산 가치</h2>
            <p className={styles.myTotalValue}>
              {(
                // 내 아이디어 가치 (이미 in_progress만 필터링되어 있음)
                ideas.reduce((total, idea) => total + (idea.total_investment || 0), 0) +
                // 투자 수익 (in_progress 상태인 아이디어만)
                seedMoneyHistory
                  .filter(history => 
                    history.type === 'investment' && 
                    history.ideas?.status === 'in_progress'
                  )
                  .reduce((total, history) => total + (history.idea_investments.value || 0), 0) +
                // 현재 예산
                (userProfile?.current_budget || 0)
              ).toLocaleString()}원
            </p>
          </div>

          {/* 현재 예산 */}
          <div className={styles.budgetDetail}>
            <p className={styles.budgetTitle}>현재 예산</p>
            <p className={styles.currentBudget}>
              {userProfile?.current_budget?.toLocaleString() || 0}원
            </p>
          </div>

          {/* 내 아이디어 가치 */}
          <div className={styles.budgetDetail}>
            <p className={styles.budgetTitle}>내 아이디어</p>
            <p className={styles.currentBudget}>
              {ideas
                .reduce((total, idea) => total + (idea.total_investment || 0), 0)
                .toLocaleString()}원
            </p>
          </div>

          {/* 투자한 금액 */}
          <div className={styles.budgetDetail}>
            <p className={styles.budgetTitle}>투자한 금액</p>
            <p className={styles.currentBudget}>
              {seedMoneyHistory
                .filter(history => history.type === 'investment')
                .reduce((total, history) => total + Math.abs(history.adjustment), 0)
                .toLocaleString()}원
            </p>
          </div>

          {/* 투자 수익 (in_progress 상태인 아이디어만) */}
          <div className={styles.budgetDetail}>
            <p className={styles.budgetTitle}>투자 수익</p>
            <p className={styles.currentBudget}>
              {(
                seedMoneyHistory
                  .filter(history => 
                    history.type === 'investment' && 
                    history.ideas?.status === 'in_progress'
                  )
                  .reduce((total, history) => total + (history.idea_investments.value || 0), 0) -
                seedMoneyHistory
                  .filter(history => history.type === 'investment')
                  .reduce((total, history) => total + Math.abs(history.adjustment), 0)
              ).toLocaleString()}원
            </p>
          </div>
        </div>

        <div className={styles.section}>
          {/* 시드머니 사용 내역 */}
            <h2>시드머니 사용 내역</h2>
            <div className={styles.historyList}>
              {seedMoneyHistory.map((history) => (
                <div key={history.id} className={styles.historyItem}>
                  <div className={styles.historyInfo}>
                    <span className={`${styles.historyType} ${getHistoryTypeLabel(history.type).className}`}>
                      {getHistoryTypeLabel(history.type).label}
                    </span>
                    <div className={styles.titleContainer}>
                      <span className={styles.historyTitle}>
                        {history.ideas?.title}
                      </span>
                    </div>
                    <span className={styles.historyDate}>
                      {new Date(history.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className={styles.historyValue}>
                    <span className={styles.adjustment}>
                      {history.adjustment < 0 ? '' : '+'}
                      {history.adjustment.toLocaleString()}원
                    </span>
                    {history.type === 'investment' && (
                      <span className={styles.currentValue}>
                        현재 {history.ideas?.status === 'in_progress' 
                          ? (history.idea_investments.value || 0).toLocaleString()
                          : '0'}원
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
        </div>

        <div className={styles.logoutSection}>
          <Button onClick={handleLogout}>로그아웃</Button>
          <p className={styles.userEmail}>{session?.user?.email}</p>
        </div>
      </main>

      <TabMenu />
    </div>
  );
}
export default MyPage; 